import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import { Field, CheckboxField, Honeypot, FormStatus } from '@/components/base/FormFields';
import PageHero from '@/components/feature/PageHero';
import { useFormSubmit } from '@/hooks/useFormSubmit';
import { CHECKLIST_BENEFITS } from '@/data/checklist';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db73i0gd6mcdj80oo790';
const FORM_ID = 'customer_checklist';
const RESULT_KEY = 'kor38_checklist';
const THANK_YOU_PATH = '/free-event-checklist/thank-you';

export default function FreeEventChecklist() {
  const navigate = useNavigate();
  const { status, submitting, errorMessage, handleSubmit } = useFormSubmit();
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    if (status === 'success') {
      try {
        sessionStorage.setItem(
          RESULT_KEY,
          JSON.stringify({ marketingOptIn: marketingConsent, ts: Date.now() }),
        );
      } catch {
        /* storage unavailable — thank-you page falls back to a neutral state */
      }
      navigate(THANK_YOU_PATH);
    }
  }, [status, marketingConsent, navigate]);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (submitting) {
      event.preventDefault();
      return;
    }
    const stamp = event.currentTarget.elements.namedItem('submitted_at');
    if (stamp instanceof HTMLInputElement) {
      stamp.value = new Date().toISOString();
    }
    void handleSubmit(event, SUBMIT_ADDR);
  };

  return (
    <>
      <PageHero
        eyebrow="Your Complimentary Event Guide"
        title="The Event Supplier Checklist"
        intro={
          <>
            <p className="font-label text-sm uppercase tracking-[0.14em] text-accent-600">
              10 Questions to Ask Before You Book Any Event Supplier
            </p>
            <p>
              Planning a wedding, celebration or corporate event? Discover what to ask, what to check
              and how to avoid costly surprises before booking your suppliers.
            </p>
          </>
        }
      />

      <section className="w-full px-4 md:px-8 lg:px-12 pb-20 md:pb-28 bg-background-50">
        <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-6">
            <div className="w-full rounded-lg overflow-hidden border border-background-200 bg-background-100">
              <img
                src="https://storage.helloreaddy.io/project_files/70bd3036-f013-4766-9098-d293cc2afad4/06851384-c9fc-45d1-98b0-736a21177861_compressed_Luxury-Event-Supplier-Checklist.webp"
                alt="The KOR38 branded Event Supplier Checklist guide cover"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <h2 className="mt-8 font-heading text-xl md:text-2xl text-foreground-950">
              What&rsquo;s inside your checklist
            </h2>
            <ul className="mt-5 space-y-3">
              {CHECKLIST_BENEFITS.map((benefit) => (
                <li key={benefit} className="flex items-start gap-3 text-base leading-relaxed text-foreground-700">
                  <i className="ri-checkbox-circle-line mt-0.5 text-lg text-primary-400" aria-hidden="true"></i>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6">
            <form
              id={FORM_ID}
              name={FORM_ID}
              data-netlify="true"
              data-readdy-form={FORM_ID}
              method="post"
              onSubmit={onSubmit}
              className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-8 space-y-5"
            >
              <input type="hidden" name="form-name" value={FORM_ID} />
              <input type="hidden" name="submitted_at" defaultValue="" />

              <div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
                  Get Your Free Checklist
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  Enter your details and choose your version on the next page.
                </p>
              </div>

              <Field
                label="First name"
                name="first_name"
                required
                autoComplete="given-name"
                placeholder="Your first name"
              />
              <Field
                label="Last name"
                name="last_name"
                required
                autoComplete="family-name"
                placeholder="Your last name"
              />
              <Field
                label="Email address"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
              />

              <CheckboxField
                name="marketing_consent"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                label={
                  <>
                    Yes, I&rsquo;d like to receive useful event supplier tips, updates and occasional
                    offers from KOR38. I understand I can unsubscribe at any time.{' '}
                    <span className="text-foreground-600">(Optional)</span>
                  </>
                }
              />

              <CheckboxField
                name="privacy_ack"
                required
                label={
                  <>
                    I have read and understand the{' '}
                    <Link to="/privacy-policy" className="text-primary-300 underline underline-offset-2">
                      Privacy Policy
                    </Link>{' '}
                    and agree to KOR38 using my details to provide this download.{' '}
                    <span className="text-primary-400">*</span>
                  </>
                }
              />

              <p className="text-sm leading-relaxed text-foreground-600">
                Your checklist is free whether or not you subscribe. We&rsquo;ll use your details to
                provide your requested download. See our{' '}
                <Link to="/privacy-policy" className="text-primary-300 underline underline-offset-2">
                  Privacy Policy
                </Link>
                .
              </p>

              <Honeypot />

              <div className="space-y-4">
                <FormStatus
                  status={status}
                  successMessage="Thank you — your checklist is ready."
                  errorMessage={errorMessage}
                />
                <Button type="submit" variant="primary" size="lg" disabled={submitting} className="w-full">
                  {submitting ? 'Sending…' : 'Get My Free Checklist'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}